import { assign, find, get, has, replace, set } from 'lodash';
import {
  IPLocation,
  Status,
  OperationalState,
  AdministrativeState,
  RunType,
  IMetaDataItem,
  IFailingKpi,
  ITest,
  IService,
  TestType,
  IMonitor,
  Platform,
  ICoordinates,
  TestTypeValue
} from './types';
import { IEntity } from '@nrwl/common-lib';
import { Agent } from './agent.model.factory';

export class Monitor implements IEntity {
  isDeleted: boolean;
  monitorData: IMonitor;
  alerts: Array<any>; //eslint-disable-line
  ipLocations: Array<IPLocation>;
  escapedName: string;
  agent: Agent;
  destination: Agent;

  constructor(dataIn: IMonitor) {
    this.escapedName = this.escapeString(dataIn.alias);
    this.monitorData = <IMonitor>{};
    this.alerts = [];
    this.ipLocations = [];
    this.setMonitor(dataIn);
    this.agent = undefined;
    this.destination = undefined;
    this.isDeleted = false;
  }

  //-------------------------------------------------Monitor data-------------------------------------------------------
  getMonitor(): IMonitor {
    return this.monitorData;
  }

  getId(): string {
    return this.monitorData.id || null;
  }

  getName(): string {
    return this.monitorData.alias || null;
  }

  getEscapedName(): string {
    return this.escapedName;
  }

  escapeString(input: string): string {
    return JSON.stringify(input).replace(/^"(.*)"$/, '$1');
  }

  getAgentId(): string {
    return this.monitorData.agentSelector?.id || null;
  }

  getAlias(): string {
    return this.monitorData.alias || null;
  }

  getTests(): ITest {
    return this.monitorData.lastTest || null;
  }

  getStatus(): Status {
    return this.monitorData.status || 'none';
  }

  getOperationalState(): OperationalState {
    return this.monitorData.operationalState || null;
  }

  getOperationalStateMessage(): string {
    return this.monitorData.operationalStateMessage || null;
  }

  getAdministrativeState(): AdministrativeState {
    return this.monitorData.administrativeState || null;
  }

  getMonitorRunType(): RunType {
    return this.monitorData.runType || '';
  }

  getTestConfig(): string {
    return this.monitorData.testConfig || '';
  }

  getMonitorProxy(): string {
    const metaData_proxy = find(this.monitorData.metaData, {
      key: 'web-proxy'
    });
    if (metaData_proxy) {
      const pConfig = JSON.parse(metaData_proxy.value).config;
      if (pConfig.protocol && pConfig.address && pConfig.port) {
        return `${pConfig.protocol}://${pConfig.address}:${pConfig.port}`;
      }
    }
    return;
  }

  getSipProxyOriginator(): IMetaDataItem {
    return find(this.monitorData.metaData, { key: 'sip-proxy-originator' });
  }

  getSipProxyDestination(): IMetaDataItem {
    return find(this.monitorData.metaData, { key: 'sip-proxy-destination' });
  }

  hasAnySipProxy(): boolean {
    const sipProxyInfo = this.getSipProxyOriginator() || this.getSipProxyDestination();
    if (sipProxyInfo && sipProxyInfo.value) {
      const proxy = JSON.parse(sipProxyInfo.value);
      return has(proxy['sip-proxy'], 'proxy-server-config');
    }
    return false;
  }

  isRunOnceTest(): boolean {
    return this.getMonitorRunType() === 'on-demand';
  }

  getIpLocationsList(): Array<IPLocation> {
    return this.ipLocations.filter(ipLocation => ipLocation.status !== 'none');
  }

  getFailingKpis(): Array<IFailingKpi> {
    return this.monitorData.failingKpis || [];
  }

  setMonitor(dataIn: IMonitor): void {
    assign(this.monitorData, dataIn);
    this.escapedName = this.escapeString(this.monitorData.alias);
  }

  setName(name: string): void {
    set(this.monitorData, 'name', name);
  }

  setTests(tests: ITest): void {
    set(this.monitorData, 'lastTest', tests);
  }

  setStatus(status: Status): void {
    set(this.monitorData, 'status', status);
  }

  setLastCriticalTest(test: ITest): void {
    set(this.monitorData, 'lastCriticalTest', test);
  }

  setLastWarningTest(test: ITest): void {
    set(this.monitorData, 'lastWarningTest', test);
  }

  setLastGoodTest(test: ITest): void {
    set(this.monitorData, 'lastGoodTest', test);
  }

  setLastNoneTest(test: ITest): void {
    set(this.monitorData, 'lastNoneTest', test);
  }

  setOperationalState(state: OperationalState): void {
    set(this.monitorData, 'operationalState', state);
  }

  setAdministrativeState(state: AdministrativeState): void {
    set(this.monitorData, 'administrativeState', state);
  }

  setStateMessage(message: string): void {
    set(this.monitorData, 'operationalStateMessage', message);
  }

  setIpLocationsList(data: Array<IPLocation>): void {
    this.clearIpLocations();
    assign(this.ipLocations, data);
  }

  setFailingKpis(failingKpis: Array<IFailingKpi>): void {
    set(this.monitorData, 'failingKpis', failingKpis);
  }

  setOriginatorSiteName(siteName: string): void {
    set(this.monitorData, 'originatorSite', siteName);
  }

  setDestinationSiteName(siteName: string): void {
    set(this.monitorData, 'destinationSite', siteName);
  }

  setOriginatorSiteId(siteId: string): void {
    set(this.monitorData, 'src-site-id', siteId);
  }

  setDestinationSiteId(siteId: string): void {
    set(this.monitorData, 'dst-site-id', siteId);
  }

  //--------------------------------------------------Alerts data-------------------------------------------------------
  //eslint-disable-next-line
  getAlerts(): Array<any> {
    return this.alerts;
  }

  //eslint-disable-next-line
  setAlerts(data: Array<any>): void {
    assign(this.alerts, data);
  }

  //--------------------------------------------------Service-----------------------------------------------------------
  getService(): IService {
    return this.monitorData.service || null;
  }

  getServiceId(): string {
    return this.monitorData.service?.id || null;
  }

  getServiceIcon(): string {
    switch (get(this.monitorData, 'testType', null)) {
      case 'NQ':
      case 'networkquality':
      case 'network-quality': {
        return 'images/network.svg';
      }
      case 'SC':
      case 'servicecontinuity':
      case 'service-continuity': {
        return 'images/services.svg';
      }
      case 'VoIP':
      case 'voip': {
        return 'images/voip.svg';
      }
      case 'truespeed': {
        return 'images/truespeed.svg';
      }
      default:
        return 'images/services.svg';
    }
  }

  getServiceLabel(): string {
    return this.monitorData.service?.label || null;
  }

  getDestinationAgentId(): string {
    return this.monitorData.service?.agentSelector?.id || null;
  }

  getDestinationAgentAlias(): string {
    return this.monitorData.service?.agentSelector?.alias || '';
  }
  getSIP(): string {
    return this.monitorData.service?.phonenumber || '';
  }
  getDestinationAgentLocation(): string {
    return this.monitorData.destinationSite || null;
  }

  getOriginatorSiteId(): string {
    return this.monitorData['src-site-id'] || null;
  }

  getDestinationSiteId(): string {
    return this.monitorData['dst-site-id'] || null;
  }

  getServiceUrl(): string {
    return this.monitorData.service?.url || '';
  }

  get serviceType(): string {
    return this.getServiceUrl().indexOf('tcp://') > -1 ? 'tcp' : 'default';
  }

  getOriginatorSiteName(): string {
    return this.monitorData.originatorSite || null;
  }

  getDestinationSiteName(): string {
    return this.monitorData.destinationSite || null;
  }

  //----------------------------------------------------Agent data--------------------------------------------------------
  getAgent(): Agent {
    return get(this.agent, null);
  }

  getAgentAlias(): string {
    return this.changeDestroyMsg(get(this, 'monitorData.agentSelector.alias', ''));
  }

  getRegionId(): string {
    return this.monitorData.agentSelector?.regionName || '';
  }

  getLocation(): string {
    return this.monitorData.originatorSite || '';
  }

  getLocationLonLat(): ICoordinates {
    return this.monitorData.agentSelector?.location?.coordinate || null;
  }

  getPlatform(): Platform {
    return get(this.getParamByName('platform', this.agent?.agentData)?.value, null);
  }

  setAgent(agent: Agent): void {
    this.agent = agent;
  }

  //--------------------------------------------Destination agent data--------------------------------------------------
  getDestination(): Agent {
    return this.destination || null;
  }

  getDestinationAlias(): string {
    return this.changeDestroyMsg(get(this.destination, 'agentSelector.alias', ''));
  }

  getDestinationPlatform(): Platform {
    return this.destination ? this.destination.getPlatform() : null;
  }

  getOriginatorPlatForm(): Platform {
    return this.agent ? this.agent.getPlatform() : null;
  }
  getDestinationLocation(): string {
    return this.monitorData.destinationSite || null;
  }

  getDestinationLocationLonLat(): ICoordinates {
    return this.monitorData.service?.agentSelector?.location?.coordinate || null;
  }

  setDestination(agent: Agent): void {
    this.destination = agent;
  }

  //eslint-disable-next-line
  getParamByName(name: string, agent: Agent): any {
    return find(get(agent, 'params', []), { name: name });
  }

  //-----------------------------------------------Destination info-------------------------------------------------------
  getDestinationLabel(): string {
    if (this.isAgentBased()) {
      return this.getDestinationAlias() || this.getDestinationLocation();
    }
    return this.getServiceLabel();
  }

  getDestinationUrl(): string {
    return this.getServiceUrl();
  }

  isAppDepended(appId: number): boolean {
    return this.monitorData['application-id'] === appId;
  }

  //---------------------------------------------------Status-----------------------------------------------------------
  isEnabled(): boolean {
    return this.getAdministrativeState() === 'Enabled';
  }

  isObserved(): boolean {
    return this.getOperationalState() === 'running';
  }

  isAgentBased(): boolean {
    return (<Array<TestType>>['SC', 'multistep']).indexOf(this.getTestType()) === -1;
  }

  //----------------------------------------------------Tools-------------------------------------------------------------
  getTestType(): TestType {
    switch (get(this.monitorData, 'testType', null)) {
      case 'network-quality': {
        return TestTypeValue.NETWORK_QUALITY;
      }
      case 'service-continuity': {
        return TestTypeValue.SERVICE_CONTINUITY;
      }
      case 'voip': {
        return TestTypeValue.VOIP;
      }
      case 'truespeed': {
        return TestTypeValue.TRUESPEED;
      }
      case 'multistep': {
        return TestTypeValue.MULTISTEP;
      }
      default:
        return '';
    }
  }

  getTestName(): TestType {
    return get(this.monitorData, 'alias', null);
  }
  getTestId(): TestType {
    return get(this.monitorData, 'testId', null);
  }
  getTestParams(testId: string): string {
    const testConfig = this.getTestConfig();
    let optionValue = '';
    if (testConfig) {
      const testConfigObj = JSON.parse(testConfig);
      const primaryConfig = testConfigObj?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        const testOptions = primaryConfig.schedules.schedule[0].action[0].option;
        const option = testOptions.find(opt => opt.id === testId);
        optionValue = option ? option.value : '';
      }
    }
    return optionValue;
  }
  getScheduledParams(): number {
    const testConfig = this.getTestConfig();
    let scheduleValue = 0;
    if (testConfig) {
      const testConfigObj = JSON.parse(testConfig);
      const primaryConfig = testConfigObj?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        const event = primaryConfig.events.event[0];
        scheduleValue = event.periodic.interval;
      }
    }
    return scheduleValue;
  }

  private getScheduleDataParams() {
    let schedulerData = '';
    const testConfig = this.getTestConfig();
    if (testConfig) {
      const testConfigObj = JSON.parse(testConfig);
      const primaryConfig = testConfigObj?.config?.[0]?.primaryConfig['ietf-lmap-control:lmap'];
      if (primaryConfig) {
        schedulerData = primaryConfig.events.event[0].periodic;
      }
    }
    return schedulerData;
  }

  private clearIpLocations(): void {
    this.ipLocations.length = 0;
  }

  /*
   * Change _DESTROYED_ from given string to _EXPIRED_ for troubleshooting monitors
   */
  private changeDestroyMsg(alias: string): string {
    if (alias && this.getMonitorRunType() === 'on-demand') {
      return replace(alias, '_DESTROYED_', '_EXPIRED_');
    }
    return alias;
  }
}
